import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { H3, Overline, Separator, Subheading, InfoSheetPanel, Row, Button, usePageWidth } from '@fs/zion-ui'
import { useFeatureFlag } from '@fs/zion-flags'
import { useSlsDispatchContext, useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import PersonReviewCard from '../personReviewCard/PersonReviewCard'
import { getAllPersons } from '../../utils/utils'
import { showEventsHighlights } from '../../utils/highlightUtils'
import { saveAttributionToRecord } from '../../utils/saveFunctions'
import { useFamilyReviewDispatchContext, STEPS, useFamilyReviewStateContext } from '../../providers/FamilyReviewContext'
import ErrorOverlay from '../../../../../shared/components/errorOverlay/ErrorOverlay'
import { saveGetInvolvedStats } from '../../../../../shared/services/recordEnhancement/recordEnhancementService'
import { defaultInitialState } from '../../providers/FamilyReviewReducer'

export default function StepFour({ currentTask, handleOpenFinishedOverlay, editPersonOverlay, stepFourOverlay }) {
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const saveChangesFlag = useFeatureFlag('rip_sharedImageViewer_saveChanges')
  const state = useFamilyReviewStateContext()
  const dispatch = useFamilyReviewDispatchContext()
  const zionSLSState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const persons = getAllPersons(currentTask)

  const [saveError, setSaveError] = useState()

  const handleDone = async () => {
    try {
      saveGetInvolvedStats({
        partitionKey: currentTask?.partitionKey,
        taskType: 'Family Review',
        action: state.statsTrackingUserAction,
      })
      dispatch({ type: 'SET_STATS_TRACKING_USER_ACTION', value: defaultInitialState.statsTrackingUserAction })
      await saveAttributionToRecord({
        currentTask,
        userId: state.userId,
        zionSLSState,
        zionSlsDispatch,
        saveChangesFlag,
      })
      handleOpenFinishedOverlay()
    } catch (err) {
      console.error(err)
      setSaveError(err)
    }
  }

  const handleStepFourClose = () => {
    stepFourOverlay.close()
    dispatch({ type: 'SET_CURRENT_STEP', value: STEPS.STEP_THREE })
    showEventsHighlights({
      h,
      currentTask,
      highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
      eventIds: [state.stepThree.eventCards[state.stepThree.eventCards.length - 1].eventId], // this is only empty if forceFieldIds is not empty
      isSmallScreen,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
    })
  }

  const handleClickBack = () => {
    handleStepFourClose()
  }

  const userClosedPanel = () => {
    handleStepFourClose()
  }

  return (
    <>
      <InfoSheetPanel
        {...{ ...stepFourOverlay, close: userClosedPanel }}
        headingColor="purple"
        title={t('family-review.step5.heading', 'Final Review')}
        footer={
          <Row alignX="end">
            <Button onClick={handleClickBack} data-testid="step-four-back-button" linkName="Step Four Go Back">
              {t('navigation.back-button', 'Back')}
            </Button>
            <Button emphasis="high" onClick={handleDone} data-testid="step-four-done-button" linkName="Step Four Done">
              {t('navigation.submit-button', 'Submit')}
            </Button>
          </Row>
        }
      >
        {stepFourOverlay.isOpen && (
          <>
            <H3 size="H5">
              <Overline>{t('family-review.stepXof4', 'Step {step} of 4', { step: '4' })}</Overline>
              {t('family-review.step5.subheading', 'Is this review ready to submit?')}
              <Subheading>
                {t('family-review.step5.content', 'If you need to make an edit, select the pencil icon.')}
              </Subheading>
            </H3>
            <Separator size="sm" />
            {persons.map((person, index, array) => {
              return (
                <div key={person?.id}>
                  <PersonReviewCard person={person} editPersonOverlay={editPersonOverlay} currentTask={currentTask} />
                  {index < array.length - 1 && <Separator size="xs" />}
                </div>
              )
            })}
          </>
        )}
      </InfoSheetPanel>
      <ErrorOverlay
        error={saveError}
        setError={setSaveError}
        title={t('save-error-overlay.heading', 'Unable to Save Changes')}
        message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
      />
    </>
  )
}
